// The enterprise tenant shell (FRONTEND-BLUEPRINT §1): top app bar (brand + Organisatie/Verordening context
// selectors + notifications + user menu) → persona tab strip (6 personas) → left sub-nav for the active
// persona → content Outlet. Replaces the flat link row. Built on the shadcn/ui design system (preset
// b1au7YYCm). The Organisatie/Verordening selectors render the current scope; multi-org/multi-verordening
// switching wiring is round-2 (today a tenant is single-org, RLS-scoped server-side).
import { useEffect, useState } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Bell, ChevronDown, LogOut } from "lucide-react";
import { useSession } from "../lib/session";
import { useI18n } from "../lib/i18n";
import { hasPermission } from "@sympllex/auth";
import { AUTH_BASE } from "../lib/env";
import { PERSONAS, ACCOUNT_MENU, type Persona } from "../lib/persona-nav";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// Several routes live under more than one persona (e.g. /risico is in BOTH Inspecteur and Controller; /dsar in
// BOTH Inspecteur and Tickets). Picking the first persona that matched threw the user into Inspecteur the
// moment they clicked such a shared sub-nav item from Controller/Tickets. So persona is STICKY: among the
// personas that serve the current path, prefer the one you're already in — only switch when the new path is
// NOT served by the current persona (a genuine persona change, e.g. clicking a tab). Cold deep-link falls back
// to first-match.
function useActivePersona(): Persona {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [sticky, setSticky] = useState<string | null>(null);
  const matches = PERSONAS.filter((p) => p.home === path || p.items.some((i) => i.to !== "/" && path.startsWith(i.to)));
  const current = (sticky ? matches.find((p) => p.id === sticky) : undefined) ?? matches[0] ?? PERSONAS[0]!;
  useEffect(() => { if (current.id !== sticky) setSticky(current.id); }, [current.id, sticky]);
  return current;
}

export function AppShell() {
  const session = useSession();
  const { t, lang, setLang } = useI18n();
  const active = useActivePersona();
  const can = (perm?: string) => !perm || hasPermission(session, perm as Parameters<typeof hasPermission>[1]);
  const logout = async () => {
    await fetch(`${AUTH_BASE}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    window.location.href = `${AUTH_BASE}/auth/login`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top app bar */}
      <header className="border-b bg-card">
        <div className="flex h-14 items-center gap-4 px-5">
          <Link to="/" className="shrink-0 whitespace-nowrap text-[15px] font-semibold tracking-tight text-primary">Aquino Compliance</Link>
          <div className="ml-2 hidden items-center gap-2 md:flex">
            <Select value="org"><SelectTrigger size="sm" className="w-[200px]" aria-label={t("ctx.organisation")}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="org">{t("ctx.organisation")}: {session.user.email.split("@")[1] ?? "—"}</SelectItem></SelectContent></Select>
            <Select value="gdpr"><SelectTrigger size="sm" className="w-[220px]" aria-label={t("ctx.regulation")}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="gdpr">{t("ctx.regulation")}: GDPR / AVG</SelectItem></SelectContent></Select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex rounded-md border" role="group" aria-label="Language">
              {(["nl", "en"] as const).map((l) => (
                <button key={l} type="button" aria-pressed={lang === l} onClick={() => setLang(l)}
                  className={cn("px-2 py-0.5 text-xs", lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>{l.toUpperCase()}</button>
              ))}
            </div>
            <Button variant="ghost" size="icon" aria-label={t("notif.aria")} asChild><Link to="/"><Bell className="size-4" /></Link></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">{session.user.email}<ChevronDown className="size-3.5" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t("nav.account")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ACCOUNT_MENU.filter((i) => can(i.perm)).map((i) => (
                  <DropdownMenuItem key={i.to} asChild><Link to={i.to}>{t(i.label)}</Link></DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}><LogOut className="size-4" />{t("nav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Persona tab strip */}
        <nav className="flex gap-1 px-3" aria-label="Personas">
          {PERSONAS.map((p) => {
            const Icon = p.icon;
            const isActive = p.id === active.id;
            return (
              <Link key={p.id} to={p.home}
                className={cn("flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm",
                  isActive ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
                <Icon className="size-4" />{t(p.label)}
              </Link>
            );
          })}
        </nav>
      </header>

      <div className="flex">
        {/* Left sub-nav for the active persona */}
        <aside className="w-56 shrink-0 border-r bg-card/40 p-3">
          <ul className="space-y-0.5">
            {active.items.filter((i) => can(i.perm)).map((i) => (
              <li key={i.to}>
                <Link to={i.to}
                  // muted color lives in inactiveProps (NOT the base) so the active item never carries both
                  // text-muted-foreground AND text-foreground — that class conflict rendered the active link at
                  // 4.34:1 muted-on-accent, failing WCAG AA (caught by wcag.spec). Active = text-foreground only.
                  className="block rounded-md px-3 py-1.5 text-sm hover:bg-accent hover:text-foreground"
                  activeProps={{ className: "bg-accent font-medium text-foreground" }}
                  inactiveProps={{ className: "text-muted-foreground" }}
                  activeOptions={{ exact: i.to === "/" }}>
                  {t(i.label)}
                </Link>
              </li>
            ))}
          </ul>
        </aside>
        <main className="min-w-0 flex-1 px-6 py-6"><Outlet /></main>
      </div>
    </div>
  );
}
